import { type TCrossLinkTag } from '@core/models'

// Backend tag prefix → category-page hub slug (audio-web `shared/lib/category`). An unprefixed or
// unknown-prefix tag falls back to the `music` hub, matching audio-web `parsePrefixedTag`.
const PREFIX_TO_HUB: Record<string, string> = {
    music: 'music',
    podcast: 'podcasts',
    'sound-design': 'sound-effect',
}
const DEFAULT_HUB = 'music'

const normalizeTag = (tag: string): string =>
    tag.toLowerCase().replace(/#/g, '').replace(/\s+/g, ' ').trim()

const slugify = (text: string): string => text.toLowerCase().replace(/ /g, '-')

// `crossLinkTag.id` is a possibly-prefixed tag, e.g. `sound-design:rain` or a bare `acoustic`.
const getCrossLinkTagUrl = (crossLinkTagId: string): string => {
    const colonIndex = crossLinkTagId.indexOf(':')
    const hub = colonIndex === -1 ? undefined : PREFIX_TO_HUB[crossLinkTagId.slice(0, colonIndex)]

    return hub === undefined
        ? `/category/${DEFAULT_HUB}/${slugify(crossLinkTagId)}`
        : `/category/${hub}/${slugify(crossLinkTagId.slice(colonIndex + 1))}`
}

const buildCrossLinkMap = (crossLinkTags: TCrossLinkTag[]): Map<string, TCrossLinkTag> =>
    new Map(
        crossLinkTags.map((crossLinkTag): [string, TCrossLinkTag] => {
            const colonIndex = crossLinkTag.id.indexOf(':')
            const tagPart =
                colonIndex === -1 ? crossLinkTag.id : crossLinkTag.id.slice(colonIndex + 1)

            return [normalizeTag(tagPart), crossLinkTag]
        }),
    )

export type TCollectionTagLink = {
    label: string
    href?: string
}

// Resolves up to `limit` display tags to chips: cross-linked tags carry a category-page href, the
// rest are plain. `extraCount` is the "+N" overflow shown after the truncated list.
export const buildCollectionTagLinks = (
    tags: string[],
    crossLinkTags: TCrossLinkTag[],
    limit: number,
): { links: TCollectionTagLink[]; extraCount: number } => {
    const crossLinkMap = buildCrossLinkMap(crossLinkTags)

    const links = tags.slice(0, limit).map(tag => {
        const crossLink = crossLinkMap.get(normalizeTag(tag))

        return { label: tag, href: crossLink ? getCrossLinkTagUrl(crossLink.id) : undefined }
    })

    return { links, extraCount: Math.max(0, tags.length - limit) }
}
